import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useCrypto } from '../context/CryptoContext';
import CoinSelector from '../components/CoinSelector';
import {
    LuShield,
    LuTrendingUp,
    LuTrendingDown,
    LuTriangleAlert,
    LuCheck,
    LuInfo,
    LuPercent,
    LuDollarSign,
    LuRefreshCw,
    LuPlus,
    LuTrash2,
    LuChartPie,
    LuActivity,
    LuDroplet,
    LuThermometer,
    LuZap
} from 'react-icons/lu';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler, ArcElement);

function RiskAnalysis({ mode = 'fiat' }) {
    const { lang } = useLanguage();
    const { coins, connected } = useCrypto();
    const isCrypto = mode === 'crypto';

    // Fiat mode form data
    const [formData, setFormData] = useState({
        investment: '',
        expectedReturn: '10',
        volatility: '15',
        timeHorizon: '12',
        riskTolerance: 'moderate'
    });

    // Crypto mode - portfolio holdings
    const [portfolio, setPortfolio] = useState([
        { coin: 'BTCUSDT', amount: '', allocation: 0 }
    ]);

    // Fear & Greed Index (simulated based on market data)
    const [fearGreedIndex, setFearGreedIndex] = useState(50);
    const [marketSentiment, setMarketSentiment] = useState('neutral');

    const [results, setResults] = useState(null);
    const [cryptoResults, setCryptoResults] = useState(null);

    const riskTolerances = [
        { id: 'conservative', label: lang === 'tr' ? 'Dusuk Risk' : 'Conservative', volatilityMax: 10, color: '#10b981' },
        { id: 'moderate', label: lang === 'tr' ? 'Orta Risk' : 'Moderate', volatilityMax: 20, color: '#f59e0b' },
        { id: 'aggressive', label: lang === 'tr' ? 'Yuksek Risk' : 'Aggressive', volatilityMax: 35, color: '#ef4444' }
    ];

    // Calculate Fear & Greed Index based on market data
    useEffect(() => {
        if (isCrypto && coins.length > 0) {
            // Calculate based on price changes
            const avgChange = coins.slice(0, 10).reduce((sum, c) => sum + (c.priceChangePercent || 0), 0) / 10;

            // Simple Fear & Greed calculation
            // -10% or worse = Extreme Fear (0-20)
            // -5% to -10% = Fear (20-40)
            // -5% to +5% = Neutral (40-60)
            // +5% to +10% = Greed (60-80)
            // +10% or better = Extreme Greed (80-100)
            let index;
            if (avgChange <= -10) index = Math.max(0, 10 + avgChange);
            else if (avgChange <= -5) index = 20 + (avgChange + 10) * 4;
            else if (avgChange <= 5) index = 40 + avgChange * 2;
            else if (avgChange <= 10) index = 60 + (avgChange - 5) * 4;
            else index = Math.min(100, 80 + (avgChange - 10) * 2);

            setFearGreedIndex(Math.round(index));

            if (index < 20) setMarketSentiment('extreme_fear');
            else if (index < 40) setMarketSentiment('fear');
            else if (index < 60) setMarketSentiment('neutral');
            else if (index < 80) setMarketSentiment('greed');
            else setMarketSentiment('extreme_greed');
        }
    }, [coins, isCrypto]);

    // Add coin to portfolio
    const addCoinToPortfolio = () => {
        if (portfolio.length < 10) {
            const availableCoins = coins.filter(c => !portfolio.find(p => p.coin === c.symbol));
            const nextCoin = availableCoins[0]?.symbol || 'ETHUSDT';
            setPortfolio([...portfolio, { coin: nextCoin, amount: '', allocation: 0 }]);
        }
    };

    // Remove coin from portfolio
    const removeCoinFromPortfolio = (index) => {
        if (portfolio.length > 1) {
            setPortfolio(portfolio.filter((_, i) => i !== index));
        }
    };

    // Update portfolio item
    const updatePortfolioItem = (index, field, value) => {
        const updated = [...portfolio];
        updated[index][field] = value;

        // Recalculate allocations
        const totalValue = updated.reduce((sum, item) => {
            const coin = coins.find(c => c.symbol === item.coin);
            const price = coin?.price || 0;
            return sum + (parseFloat(item.amount) || 0) * price;
        }, 0);

        updated.forEach(item => {
            const coin = coins.find(c => c.symbol === item.coin);
            const price = coin?.price || 0;
            const value = (parseFloat(item.amount) || 0) * price;
            item.allocation = totalValue > 0 ? (value / totalValue) * 100 : 0;
        });

        setPortfolio(updated);
    };

    // Calculate crypto portfolio risk
    const calculateCryptoRisk = () => {
        const portfolioData = portfolio.map(item => {
            const coin = coins.find(c => c.symbol === item.coin);
            if (!coin) return null;

            const amount = parseFloat(item.amount) || 0;
            const value = amount * coin.price;

            // Calculate 24h volatility from high/low
            const volatility24h = coin.highPrice && coin.lowPrice && coin.price > 0
                ? ((coin.highPrice - coin.lowPrice) / coin.price) * 100
                : 5; // Default volatility

            // Liquidity score based on volume
            const volumeScore = Math.min(100, (coin.volume / 1000000000) * 10);

            return {
                symbol: coin.symbol,
                shortName: coin.shortName,
                name: coin.name,
                amount,
                price: coin.price,
                value,
                priceChange24h: coin.priceChangePercent || 0,
                volatility24h,
                highPrice: coin.highPrice,
                lowPrice: coin.lowPrice,
                volume: coin.volume,
                liquidityScore: volumeScore
            };
        }).filter(Boolean);

        if (portfolioData.length === 0) return;

        const totalValue = portfolioData.reduce((sum, c) => sum + c.value, 0);
        if (totalValue <= 0) return;

        // Calculate weighted metrics
        let weightedVolatility = 0;
        let weightedLiquidity = 0;
        let portfolioVaR = 0;

        portfolioData.forEach(coin => {
            const weight = coin.value / totalValue;
            weightedVolatility += coin.volatility24h * weight;
            weightedLiquidity += coin.liquidityScore * weight;

            // VaR calculation (95% confidence, assuming normal distribution)
            // Daily VaR = Value * Volatility * 1.65 (95% z-score)
            const dailyVaR = coin.value * (coin.volatility24h / 100) * 1.65;
            portfolioVaR += dailyVaR;
        });

        // Diversification benefit (correlation approximation)
        const diversificationFactor = Math.max(0.5, 1 - (portfolioData.length - 1) * 0.1);
        portfolioVaR *= diversificationFactor;

        // Calculate concentration risk (Herfindahl Index)
        const herfindahl = portfolioData.reduce((sum, coin) => {
            const weight = coin.value / totalValue;
            return sum + weight * weight;
        }, 0);
        const concentrationRisk = herfindahl * 100; // 100 = single asset, lower = more diversified

        // Overall risk score (0-100, lower is better)
        const volatilityScore = Math.min(100, weightedVolatility * 5);
        const concentrationScore = concentrationRisk;
        const liquidityScore = 100 - weightedLiquidity;
        const sentimentScore = fearGreedIndex > 80 || fearGreedIndex < 20 ? 30 : 0;

        const overallRiskScore = Math.min(100, Math.max(0,
            volatilityScore * 0.4 +
            concentrationScore * 0.25 +
            liquidityScore * 0.2 +
            sentimentScore * 0.15
        ));

        // Best/Worst case scenarios (based on volatility)
        const bestCase = totalValue * (1 + weightedVolatility / 100 * 2);
        const worstCase = totalValue * (1 - weightedVolatility / 100 * 2);
        const expectedCase = totalValue * (1 + portfolioData.reduce((sum, c) => sum + (c.priceChange24h / 100) * (c.value / totalValue), 0));

        setCryptoResults({
            portfolio: portfolioData,
            totalValue,
            weightedVolatility,
            weightedLiquidity,
            portfolioVaR,
            concentrationRisk,
            overallRiskScore,
            diversificationFactor,
            bestCase,
            worstCase,
            expectedCase,
            assetCount: portfolioData.length
        });
    };

    const calculateRisk = () => {
        const investment = parseFloat(formData.investment) || 0;
        const expectedReturn = parseFloat(formData.expectedReturn) / 100;
        const volatility = parseFloat(formData.volatility) / 100;
        const months = parseInt(formData.timeHorizon);

        if (investment <= 0) return;

        // Monte Carlo simulation
        const simulations = 1000;
        const outcomes = [];

        for (let i = 0; i < simulations; i++) {
            let value = investment;
            for (let m = 0; m < months; m++) {
                const randomReturn = expectedReturn / 12 + (volatility / Math.sqrt(12)) * (Math.random() * 2 - 1) * 1.5;
                value *= (1 + randomReturn);
            }
            outcomes.push(value);
        }

        outcomes.sort((a, b) => a - b);

        const percentile = (p) => outcomes[Math.floor(outcomes.length * p)];

        const bestCase = percentile(0.95);
        const worstCase = percentile(0.05);
        const medianCase = percentile(0.5);
        const expectedValue = outcomes.reduce((a, b) => a + b, 0) / outcomes.length;

        // Value at Risk (95%)
        const var95 = investment - worstCase;

        // Sharpe Ratio (simplified)
        const riskFreeRate = 0.04;
        const sharpeRatio = (expectedReturn - riskFreeRate) / volatility;

        // Risk Score (0-100)
        const volatilityScore = Math.min(volatility / 0.5 * 100, 100);
        const returnRiskRatio = expectedReturn / volatility;
        const riskScore = Math.max(0, Math.min(100, 50 + (returnRiskRatio - 0.5) * 50 - volatilityScore * 0.3));

        // Distribution for histogram
        const buckets = 20;
        const min = Math.min(...outcomes);
        const max = Math.max(...outcomes);
        const bucketSize = (max - min) / buckets;
        const distribution = Array(buckets).fill(0);

        outcomes.forEach(v => {
            const bucketIndex = Math.min(Math.floor((v - min) / bucketSize), buckets - 1);
            distribution[bucketIndex]++;
        });

        setResults({
            investment,
            expectedValue,
            medianCase,
            bestCase,
            worstCase,
            var95,
            sharpeRatio,
            riskScore,
            distribution,
            min,
            max,
            bucketSize
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat(lang === 'tr' ? 'tr-TR' : 'en-US', {
            style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(value);
    };

    const formatPercent = (value) => {
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(2)}%`;
    };

    const getFearGreedLabel = () => {
        switch (marketSentiment) {
            case 'extreme_fear': return { label: lang === 'tr' ? 'Asiri Korku' : 'Extreme Fear', color: '#ef4444' };
            case 'fear': return { label: lang === 'tr' ? 'Korku' : 'Fear', color: '#f97316' };
            case 'neutral': return { label: lang === 'tr' ? 'Notr' : 'Neutral', color: '#eab308' };
            case 'greed': return { label: lang === 'tr' ? 'Acgozluluk' : 'Greed', color: '#84cc16' };
            case 'extreme_greed': return { label: lang === 'tr' ? 'Asiri Acgozluluk' : 'Extreme Greed', color: '#22c55e' };
            default: return { label: 'Neutral', color: '#eab308' };
        }
    };

    const getCryptoRiskLevel = (score) => {
        if (score <= 30) return { label: lang === 'tr' ? 'Dusuk Risk' : 'Low Risk', color: '#10b981', icon: LuCheck };
        if (score <= 60) return { label: lang === 'tr' ? 'Orta Risk' : 'Medium Risk', color: '#f59e0b', icon: LuInfo };
        return { label: lang === 'tr' ? 'Yuksek Risk' : 'High Risk', color: '#ef4444', icon: LuTriangleAlert };
    };

    const getCoinIcon = (shortName) => {
        return `https://assets.coincap.io/assets/icons/${shortName?.toLowerCase()}@2x.png`;
    };

    const getRiskLevel = (score) => {
        if (score >= 70) return { label: lang === 'tr' ? 'Dusuk Risk' : 'Low Risk', color: '#10b981', icon: LuCheck };
        if (score >= 40) return { label: lang === 'tr' ? 'Orta Risk' : 'Medium Risk', color: '#f59e0b', icon: LuInfo };
        return { label: lang === 'tr' ? 'Yuksek Risk' : 'High Risk', color: '#ef4444', icon: LuTriangleAlert };
    };

    const distributionChartData = results ? {
        labels: Array(20).fill(0).map((_, i) => formatCurrency(results.min + (i + 0.5) * results.bucketSize)),
        datasets: [{
            label: lang === 'tr' ? 'Olasilik' : 'Probability',
            data: results.distribution.map(d => d / 10),
            backgroundColor: 'rgba(0, 212, 255, 0.6)',
            borderColor: '#00d4ff',
            borderWidth: 1
        }]
    } : null;

    // Crypto portfolio allocation chart
    const allocationChartData = cryptoResults ? {
        labels: cryptoResults.portfolio.map(c => c.shortName),
        datasets: [{
            data: cryptoResults.portfolio.map(c => c.value),
            backgroundColor: [
                '#00ff88', '#00d4ff', '#ff6b6b', '#ffd93d', '#6bcb77',
                '#4d96ff', '#ff8b94', '#a8d8ea', '#ffaaa5', '#98ddca'
            ],
            borderWidth: 0
        }]
    } : null;

    const allocationChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: { color: '#9ca3af', font: { size: 11 } }
            }
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { display: false },
            y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#9ca3af' } }
        }
    };

    // Crypto Mode UI
    if (isCrypto) {
        return (
            <div className="risk-analysis-page crypto-mode">
                <div className="page-header home-header">
                    <div className="page-header-content">
                        <div className="page-title-group">
                            <div>
                                <h1 className="page-title">{lang === 'tr' ? 'Kripto Risk Analizi' : 'Crypto Risk Analysis'}</h1>
                            </div>
                        </div>
                        {connected && (
                            <div className="page-actions">
                                <span className="live-indicator">
                                    <LuZap className="live-icon" />
                                    <span>{lang === 'tr' ? 'Canlı Veri' : 'Live Data'}</span>
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Fear & Greed Index */}
                <div className="fear-greed-section">
                    <div className="fear-greed-card">
                        <div className="fear-greed-header">
                            <LuThermometer className="fear-greed-icon" />
                            <h3>{lang === 'tr' ? 'Piyasa Duyarlilik Endeksi' : 'Market Sentiment Index'}</h3>
                        </div>
                        <div className="fear-greed-gauge">
                            <div className="gauge-bar">
                                <div className="gauge-fill" style={{ width: `${fearGreedIndex}%`, background: getFearGreedLabel().color }} />
                                <div className="gauge-marker" style={{ left: `${fearGreedIndex}%` }}>
                                    <span className="gauge-value">{fearGreedIndex}</span>
                                </div>
                            </div>
                            <div className="gauge-labels">
                                <span>{lang === 'tr' ? 'Korku' : 'Fear'}</span>
                                <span>{lang === 'tr' ? 'Acgozluluk' : 'Greed'}</span>
                            </div>
                        </div>
                        <div className="fear-greed-status" style={{ color: getFearGreedLabel().color }}>
                            {getFearGreedLabel().label}
                        </div>
                    </div>
                </div>

                <div className="risk-content crypto-risk-content">
                    {/* Portfolio Builder */}
                    <div className="card portfolio-builder-card">
                        <div className="card-header-row">
                            <h3><LuChartPie /> {lang === 'tr' ? 'Portfoy Olustur' : 'Build Portfolio'}</h3>
                            <button className="btn btn-sm btn-outline" onClick={addCoinToPortfolio} disabled={portfolio.length >= 10}>
                                <LuPlus /> {lang === 'tr' ? 'Coin Ekle' : 'Add Coin'}
                            </button>
                        </div>

                        <div className="portfolio-items">
                            {portfolio.map((item, index) => {
                                const coin = coins.find(c => c.symbol === item.coin);
                                return (
                                    <div key={index} className="portfolio-item">
                                        <div className="portfolio-item-coin">
                                            <CoinSelector
                                                coins={coins}
                                                selectedCoin={item.coin}
                                                onSelect={(symbol) => updatePortfolioItem(index, 'coin', symbol)}
                                                lang={lang}
                                            />
                                        </div>
                                        <div className="portfolio-item-amount">
                                            <input
                                                type="number"
                                                className="form-input"
                                                placeholder={lang === 'tr' ? 'Miktar' : 'Amount'}
                                                value={item.amount}
                                                onChange={(e) => updatePortfolioItem(index, 'amount', e.target.value)}
                                            />
                                        </div>
                                        <div className="portfolio-item-value">
                                            {coin && item.amount ? formatCurrency((parseFloat(item.amount) || 0) * coin.price) : '-'}
                                        </div>
                                        <div className="portfolio-item-allocation">
                                            {item.allocation.toFixed(1)}%
                                        </div>
                                        {portfolio.length > 1 && (
                                            <button className="btn-icon btn-remove" onClick={() => removeCoinFromPortfolio(index)}>
                                                <LuTrash2 />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <button className="btn btn-primary btn-lg" onClick={calculateCryptoRisk}>
                            <LuRefreshCw /> {lang === 'tr' ? 'Risk Analizi Yap' : 'Analyze Risk'}
                        </button>
                    </div>

                    {/* Results */}
                    {cryptoResults && (
                        <>
                            {/* Risk Summary */}
                            <div className="crypto-risk-summary">
                                <div className="risk-score-card crypto" style={{ '--score-color': getCryptoRiskLevel(cryptoResults.overallRiskScore).color }}>
                                    <div className="risk-score-circle">
                                        <span className="score-value">{Math.round(cryptoResults.overallRiskScore)}</span>
                                        <span className="score-label">/100</span>
                                    </div>
                                    <div className="risk-score-info">
                                        <h4>{getCryptoRiskLevel(cryptoResults.overallRiskScore).label}</h4>
                                        <p>{lang === 'tr' ? 'Risk Skoru' : 'Risk Score'}</p>
                                    </div>
                                </div>

                                <div className="risk-metric crypto">
                                    <LuDollarSign className="metric-icon cyan" />
                                    <div className="metric-info">
                                        <span className="metric-value">{formatCurrency(cryptoResults.totalValue)}</span>
                                        <span className="metric-label">{lang === 'tr' ? 'Toplam Deger' : 'Total Value'}</span>
                                    </div>
                                </div>

                                <div className="risk-metric crypto">
                                    <LuActivity className="metric-icon yellow" />
                                    <div className="metric-info">
                                        <span className="metric-value">{cryptoResults.weightedVolatility.toFixed(2)}%</span>
                                        <span className="metric-label">{lang === 'tr' ? '24s Volatilite' : '24h Volatility'}</span>
                                    </div>
                                </div>

                                <div className="risk-metric crypto">
                                    <LuTrendingDown className="metric-icon red" />
                                    <div className="metric-info">
                                        <span className="metric-value">{formatCurrency(cryptoResults.portfolioVaR)}</span>
                                        <span className="metric-label">VaR (95%)</span>
                                    </div>
                                </div>

                                <div className="risk-metric crypto">
                                    <LuDroplet className="metric-icon blue" />
                                    <div className="metric-info">
                                        <span className="metric-value">{cryptoResults.weightedLiquidity.toFixed(0)}/100</span>
                                        <span className="metric-label">{lang === 'tr' ? 'Likidite' : 'Liquidity'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Scenarios */}
                            <div className="scenarios-grid crypto">
                                <div className="scenario-card best">
                                    <h4>{lang === 'tr' ? 'En Iyi Senaryo' : 'Best Case'}</h4>
                                    <span className="scenario-value">{formatCurrency(cryptoResults.bestCase)}</span>
                                    <span className="scenario-change positive">
                                        {formatPercent((cryptoResults.bestCase - cryptoResults.totalValue) / cryptoResults.totalValue * 100)}
                                    </span>
                                </div>
                                <div className="scenario-card median">
                                    <h4>{lang === 'tr' ? 'Beklenen' : 'Expected'}</h4>
                                    <span className="scenario-value">{formatCurrency(cryptoResults.expectedCase)}</span>
                                    <span className={`scenario-change ${cryptoResults.expectedCase >= cryptoResults.totalValue ? 'positive' : 'negative'}`}>
                                        {formatPercent((cryptoResults.expectedCase - cryptoResults.totalValue) / cryptoResults.totalValue * 100)}
                                    </span>
                                </div>
                                <div className="scenario-card worst">
                                    <h4>{lang === 'tr' ? 'En Kotu Senaryo' : 'Worst Case'}</h4>
                                    <span className="scenario-value">{formatCurrency(cryptoResults.worstCase)}</span>
                                    <span className="scenario-change negative">
                                        {formatPercent((cryptoResults.worstCase - cryptoResults.totalValue) / cryptoResults.totalValue * 100)}
                                    </span>
                                </div>
                            </div>

                            {/* Portfolio Details */}
                            <div className="crypto-details-grid">
                                {/* Allocation Chart */}
                                <div className="card allocation-chart-card">
                                    <h3>{lang === 'tr' ? 'Portfoy Dagilimi' : 'Portfolio Allocation'}</h3>
                                    <div className="chart-container" style={{ height: '200px' }}>
                                        <Doughnut data={allocationChartData} options={allocationChartOptions} />
                                    </div>
                                </div>

                                {/* Risk Breakdown */}
                                <div className="card risk-breakdown-card">
                                    <h3>{lang === 'tr' ? 'Risk Detaylari' : 'Risk Breakdown'}</h3>
                                    <div className="risk-breakdown-items">
                                        <div className="breakdown-item">
                                            <span className="breakdown-label">{lang === 'tr' ? 'Konsantrasyon Riski' : 'Concentration Risk'}</span>
                                            <div className="breakdown-bar">
                                                <div className="bar-fill" style={{ width: `${cryptoResults.concentrationRisk}%`, background: cryptoResults.concentrationRisk > 50 ? '#ef4444' : '#10b981' }} />
                                            </div>
                                            <span className="breakdown-value">{cryptoResults.concentrationRisk.toFixed(1)}%</span>
                                        </div>
                                        <div className="breakdown-item">
                                            <span className="breakdown-label">{lang === 'tr' ? 'Volatilite Riski' : 'Volatility Risk'}</span>
                                            <div className="breakdown-bar">
                                                <div className="bar-fill" style={{ width: `${Math.min(100, cryptoResults.weightedVolatility * 5)}%`, background: cryptoResults.weightedVolatility > 10 ? '#ef4444' : '#10b981' }} />
                                            </div>
                                            <span className="breakdown-value">{cryptoResults.weightedVolatility.toFixed(2)}%</span>
                                        </div>
                                        <div className="breakdown-item">
                                            <span className="breakdown-label">{lang === 'tr' ? 'Cesitlendirme Faydasi' : 'Diversification Benefit'}</span>
                                            <div className="breakdown-bar">
                                                <div className="bar-fill" style={{ width: `${(1 - cryptoResults.diversificationFactor) * 200}%`, background: '#10b981' }} />
                                            </div>
                                            <span className="breakdown-value">{((1 - cryptoResults.diversificationFactor) * 100).toFixed(0)}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Individual Coin Risk */}
                            <div className="card coin-risks-card">
                                <h3>{lang === 'tr' ? 'Coin Bazli Risk' : 'Individual Coin Risk'}</h3>
                                <div className="coin-risks-table">
                                    <div className="table-header">
                                        <span>{lang === 'tr' ? 'Coin' : 'Coin'}</span>
                                        <span>{lang === 'tr' ? 'Deger' : 'Value'}</span>
                                        <span>{lang === 'tr' ? 'Agirlik' : 'Weight'}</span>
                                        <span>{lang === 'tr' ? '24s Degisim' : '24h Change'}</span>
                                        <span>{lang === 'tr' ? 'Volatilite' : 'Volatility'}</span>
                                        <span>{lang === 'tr' ? 'Likidite' : 'Liquidity'}</span>
                                    </div>
                                    {cryptoResults.portfolio.map((coin, index) => (
                                        <div key={index} className="table-row">
                                            <span className="coin-cell">
                                                <img src={getCoinIcon(coin.shortName)} alt={coin.shortName} className="coin-mini-icon" onError={(e) => e.target.style.display = 'none'} />
                                                {coin.shortName}
                                            </span>
                                            <span>{formatCurrency(coin.value)}</span>
                                            <span>{(coin.value / cryptoResults.totalValue * 100).toFixed(1)}%</span>
                                            <span className={coin.priceChange24h >= 0 ? 'positive' : 'negative'}>
                                                {formatPercent(coin.priceChange24h)}
                                            </span>
                                            <span className={coin.volatility24h > 10 ? 'high-risk' : ''}>{coin.volatility24h.toFixed(2)}%</span>
                                            <span>{coin.liquidityScore.toFixed(0)}/100</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Fiat Mode UI (original)
    return (
        <div className="risk-analysis-page">
            <div className="page-header home-header">
                <div className="page-header-content">
                    <div className="page-title-group">
                        <div>
                            <h1 className="page-title">{lang === 'tr' ? 'Risk Analizi' : 'Risk Analysis'}</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="risk-content">
                <div className="card risk-form-card">
                    <h3>{lang === 'tr' ? 'Yatirim Parametreleri' : 'Investment Parameters'}</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label><LuDollarSign /> {lang === 'tr' ? 'Yatirim Tutari' : 'Investment Amount'}</label>
                            <input type="number" className="form-input" value={formData.investment} onChange={(e) => setFormData({...formData, investment: e.target.value})} placeholder="10000" />
                        </div>
                        <div className="form-group">
                            <label><LuTrendingUp /> {lang === 'tr' ? 'Beklenen Getiri (%)' : 'Expected Return (%)'}</label>
                            <input type="number" className="form-input" value={formData.expectedReturn} onChange={(e) => setFormData({...formData, expectedReturn: e.target.value})} step="0.1" />
                        </div>
                        <div className="form-group">
                            <label><LuPercent /> {lang === 'tr' ? 'Volatilite (%)' : 'Volatility (%)'}</label>
                            <input type="number" className="form-input" value={formData.volatility} onChange={(e) => setFormData({...formData, volatility: e.target.value})} step="0.1" />
                        </div>
                        <div className="form-group">
                            <label>{lang === 'tr' ? 'Yatirim Suresi (Ay)' : 'Time Horizon (Months)'}</label>
                            <input type="number" className="form-input" value={formData.timeHorizon} onChange={(e) => setFormData({...formData, timeHorizon: e.target.value})} />
                        </div>
                    </div>
                    <div className="risk-tolerance-section">
                        <label>{lang === 'tr' ? 'Risk Toleransi' : 'Risk Tolerance'}</label>
                        <div className="risk-tolerance-options">
                            {riskTolerances.map(rt => (
                                <button key={rt.id} className={'risk-option ' + (formData.riskTolerance === rt.id ? 'active' : '')} onClick={() => setFormData({...formData, riskTolerance: rt.id})} style={{ '--risk-color': rt.color }}>
                                    {rt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button className="btn btn-primary btn-lg" onClick={calculateRisk}><LuRefreshCw /> {lang === 'tr' ? 'Analiz Et' : 'Analyze'}</button>
                </div>

                {results && (
                    <>
                        <div className="risk-summary">
                            <div className="risk-score-card" style={{ '--score-color': getRiskLevel(results.riskScore).color }}>
                                <div className="risk-score-circle">
                                    <span className="score-value">{Math.round(results.riskScore)}</span>
                                    <span className="score-label">/100</span>
                                </div>
                                <div className="risk-score-info">
                                    <h4>{getRiskLevel(results.riskScore).label}</h4>
                                    <p>{lang === 'tr' ? 'Risk Skoru' : 'Risk Score'}</p>
                                </div>
                            </div>

                            <div className="risk-metric">
                                <LuTrendingUp className="metric-icon green" />
                                <div className="metric-info">
                                    <span className="metric-value">{formatCurrency(results.expectedValue)}</span>
                                    <span className="metric-label">{lang === 'tr' ? 'Beklenen Deger' : 'Expected Value'}</span>
                                </div>
                            </div>

                            <div className="risk-metric">
                                <LuTrendingDown className="metric-icon red" />
                                <div className="metric-info">
                                    <span className="metric-value">{formatCurrency(results.var95)}</span>
                                    <span className="metric-label">VaR (95%)</span>
                                </div>
                            </div>

                            <div className="risk-metric">
                                <LuShield className="metric-icon cyan" />
                                <div className="metric-info">
                                    <span className="metric-value">{results.sharpeRatio.toFixed(2)}</span>
                                    <span className="metric-label">Sharpe Ratio</span>
                                </div>
                            </div>
                        </div>

                        <div className="scenarios-grid">
                            <div className="scenario-card best">
                                <h4>{lang === 'tr' ? 'En Iyi Senaryo' : 'Best Case'} (95%)</h4>
                                <span className="scenario-value">{formatCurrency(results.bestCase)}</span>
                                <span className="scenario-change">+{(((results.bestCase - results.investment) / results.investment) * 100).toFixed(1)}%</span>
                            </div>
                            <div className="scenario-card median">
                                <h4>{lang === 'tr' ? 'Beklenen' : 'Median'} (50%)</h4>
                                <span className="scenario-value">{formatCurrency(results.medianCase)}</span>
                                <span className="scenario-change">{(((results.medianCase - results.investment) / results.investment) * 100).toFixed(1)}%</span>
                            </div>
                            <div className="scenario-card worst">
                                <h4>{lang === 'tr' ? 'En Kotu Senaryo' : 'Worst Case'} (5%)</h4>
                                <span className="scenario-value">{formatCurrency(results.worstCase)}</span>
                                <span className="scenario-change">{(((results.worstCase - results.investment) / results.investment) * 100).toFixed(1)}%</span>
                            </div>
                        </div>

                        <div className="card distribution-card">
                            <h3>{lang === 'tr' ? 'Sonuc Dagilimi' : 'Outcome Distribution'}</h3>
                            <div className="chart-container" style={{ height: '200px' }}>
                                <Bar data={distributionChartData} options={chartOptions} />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default RiskAnalysis;
